import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AppSelect from '../../../../components/form/base/app-select';
import { apiService } from '../../../../services/api.service';
import dateService from '../../../../services/date.service';
import { AppStore } from '../../../../store/store';
import { UserState } from '../../../../store/user.slice';
import { LabelValue } from '../../../../types/label-value.types';
import { IntermediateStatusType, StatusType } from '../../../../types/statistics.types';
import { ReactComponent as UserIcon } from '../../../../assets/icons/user.svg';
import { ReactComponent as MerchantIcon } from '../../../../assets/icons/merchant.svg';
import { ReactComponent as CardIcon } from '../../../../assets/icons/processor-card.svg';
import { ReactComponent as RefreshIcon } from '../../../../assets/icons/refresh.svg';
import TransactionsPartFilteredDataItem from '../transactions-page.part.filtered-data-item';
import AppModal from '../../../../components/app-modal-transaction';
import TableContent from '../../../../components/table-content';
import TableFilters from '../../../../components/table-filters';
import TimeFilters from '../../../../components/time-filters';
import Button from '../../../../components/button';
import { generateOptions, pendingApproval, pendingChargeback, pendingRefund, TransactionSummary } from '../../../../types/transaction.types';
import TablePagination from '../../../../components/table-pagination';
import './disputes-page.scss';
import { errorService } from '../../../../services/error.service';
import { formatAmount } from '../../../../utils/number-utils';
import { useActiveMerchantFilters } from '../../../../utils/use-active-merchant-filters';




interface Transaction {
    id: string;
    status: string;
    customerName: string;
    merchantName: string;
    processorName: string;
    amount: number;
    allowedActions: string[];
    date: string;
}

const DisputesPage = () => {
    const navigate = useNavigate();
    const [isModalVisible, setModalVisible] = useState(false);
    const [filters, setFilters] = useState({});

    const { effectiveFilters, canFetch } = useActiveMerchantFilters(filters);

    const [transactions, setTransactions] = useState<Transaction[]>();
    const [transactionsLoader, setTransactionsLoader] = useState(false);

    const [modalData, setModalData] = useState({
        id: '',
        status: '',
        newStatus: '',
    });
    const { user } = useSelector<AppStore, UserState>((state) => state.user)
    const [refreshingTransactionId, setRefreshingTransactionId] = useState<string | null>(null);
    const [resetTrigger, setResetTrigger] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1)
    const [totalTransactions, setTotalTransactions] = useState();

    const [transactionsSummary, setTransactionsSummary] = useState<TransactionSummary>();
    const [summaryLoader, setSummaryLoader] = useState(false);

    const isMerchant = (user?.roles?.includes("merchant") || user?.roles?.includes("bas_merchant_view")) ?? false;

    const [refreshData, setRefreshData] = useState(false);

    const [isDesktop, setIsDesktop] = useState(false);

    // Ref to track if a fetch is in progress to prevent duplicate calls
    const isFetchingRef = useRef(false);
    // Ref to store the last fetched filters/currentPage to avoid unnecessary refetches
    const lastFetchParamsRef = useRef<string>('');

    useEffect(() => {
        const checkScreenSize = () => {
            setIsDesktop(window.innerWidth > 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);

        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    // Polling effect: Set up interval to refresh transactions only
    useEffect(() => {
        if (!canFetch) {
            return;
        }
        const intervalId = setInterval(() => {
            // Skip if already fetching
            if (isFetchingRef.current) {
                return;
            }

            isFetchingRef.current = true;
            const pageInfo = {
                page: currentPage - 1,
                size: 10,
            };

            apiService.getTransactions({ ...effectiveFilters, intermediateStatuses: [IntermediateStatusType.PENDING_CHARGEBACK, IntermediateStatusType.PENDING_REFUND] }, pageInfo)
                .then((response) => {
                    if (response?.data) {
                        setTransactions(response.data.content || []);
                        setTotalPages(response.data.totalPages || 1);
                        setTotalTransactions(response.data.totalElements || 0);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching transactions in interval:', error);
                })
                .finally(() => {
                    isFetchingRef.current = false;
                });
        }, 60000);

        return () => clearInterval(intervalId);
    }, [effectiveFilters, currentPage, canFetch]);


    const columnsMidTransactions = [
        {
            Header: 'Trace ID',
            accessor: 'id',
            Cell: ({ value }: { value: string }) => (
                <div>
                    <span
                        className="id-text"
                        onClick={() => {
                            const url = `/app/transactions/transaction/${value}`;
                            if (isDesktop) {
                                window.open(url, '_blank');
                            } else {
                                navigate(url);
                            }
                        }}
                    >{value}</span>
                </div>
            ),
        },
        {
            Header: 'Customer',
            accessor: 'customerName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <UserIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">
                        {row.original.customerCardType} xxxx xxxx xxxx {row.original.customerLastFourCardDigits}
                    </div>
                </div>
            ),
        },
        {
            Header: 'Merchant',
            accessor: 'merchantName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <MerchantIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">{row.original.transactionReferenceCode}</div>
                </div>
            ),
        },
        {
            Header: 'Channel',
            accessor: 'processorName',
            Cell: ({ value, row }: { value: string; row: any }) => {
                return (
                    <div className="flex-column flex-gap-5">
                        <div className="flex-row flex-gap-5 flex-align-start-center">
                            <CardIcon />
                            <span>{value}</span>
                        </div>
                        <div className="secondary-text accent-gradient-text">{!isMerchant && row.original.processorTransactionId}</div>
                    </div>
                )
            },
        },
        {
            Header: 'Value',
            accessor: 'amount',
            Cell: ({ value }: { value: string }) => (
                <span className="value-text accent-gradient-text">${formatAmount(+value)}</span>
            ),
        },
        {
            Header: () => (
                <div className='flex-row flex-align-center-center'>
                    Status
                </div>
            ),
            accessor: 'status',
            Cell: ({ value, row }: { value: string; row: any }) => {

                if (isMerchant) {
                    return (<div
                        className={`status ${value?.toLowerCase()}
                 flex-row flex-align-center-center`}
                        onClick={() => navigate(`/app/transactions/transaction/${row.original.id}`)}
                    >
                        <span>{value}</span>
                    </div>)
                }

                const { status, intermediateStatus } = row.original;

                const options: LabelValue<StatusType>[] = generateOptions(status, intermediateStatus);


                return (
                    <>
                        {
                            options.length > 0 ? (
                                <AppSelect
                                    theme="status"
                                    options={options}
                                    placeholder={value}
                                    onChange={(newValue) => {
                                        setModalData({
                                            id: row.original.id,
                                            status: value,
                                            newStatus: newValue,
                                        });
                                        setModalVisible(true);
                                    }}
                                />
                            ) : (
                                <div
                                    className={`status ${value?.toLowerCase()} flex-row flex-align-center-center`}
                                >
                                    <span
                                        onClick={() => {
                                            if (value === StatusType.ERROR)
                                                navigate(`/app/transactions/transaction/${row.original.id}`)
                                        }}
                                    >{value}</span>
                                </div>

                            )
                        }
                    </>
                );
            },
        },
        {
            Header: () => (
                <div className='flex-row flex-align-center-center'>
                    Time
                </div>
            ),
            accessor: 'date',
            Cell: ({ value }: { value: string }) => (
                <div className="date-text">{dateService.formatDateForTable(value)}</div>
            ),
        },
        {
            Header: 'Actions',
            accessor: 'allowedActions',
            Cell: ({ value, row }: { value: string[]; row: any }) => {

                return (
                    <div className="flex-column flex-gap-5 flex-align-center-center">
                        {(row.original.status !== StatusType.CHARGEBACK && row.original.status !== StatusType.REFUNDED) && <RefreshIcon
                            className={`refresh-icon ${refreshingTransactionId === row.original.id ? 'rotate' : ''
                                }`}
                            onClick={() => {
                                if (refreshingTransactionId !== row.original.id)
                                    handleRefreshClick(row.original.id)
                            }}
                        />}
                    </div>
                )
            },
        },
    ];

    // Main effect: Fetch data when user, filters, or currentPage changes
    useEffect(() => {
        if (!canFetch) {
            return;
        }
        // Create a unique key for current fetch parameters to detect actual changes
        // Sort filter keys to ensure consistent stringification
        const normalizedFilters = Object.keys(effectiveFilters)
            .sort()
            .reduce((acc, key) => {
                const value = effectiveFilters[key as keyof typeof effectiveFilters];
                // Only include defined values to avoid undefined vs missing key differences
                if (value !== undefined && value !== null && value !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {} as Record<string, any>);

        const fetchKey = JSON.stringify({
            userId: user?.id,
            filters: normalizedFilters,
            currentPage
        });

        // Skip if parameters haven't actually changed (even if same object reference)
        if (lastFetchParamsRef.current === fetchKey) {
            return;
        }

        // Update last fetch params immediately to prevent duplicate calls
        // This must happen before any async operations
        lastFetchParamsRef.current = fetchKey;

        // Mark as fetching for this new request
        isFetchingRef.current = true;

        // Fetch transactions
        setTransactionsLoader(true);
        const pageInfo = {
            page: currentPage - 1,
            size: 10,
        };
        const transactionsPromise = apiService.getTransactions({ ...effectiveFilters, intermediateStatuses: [IntermediateStatusType.PENDING_CHARGEBACK, IntermediateStatusType.PENDING_REFUND] }, pageInfo)
            .then((response) => {
                // Only update if this fetch is still current
                if (lastFetchParamsRef.current === fetchKey && response?.data) {
                    console.log('Setting disputes transactions:', {
                        content: response.data.content?.length || 0,
                        totalPages: response.data.totalPages,
                        totalElements: response.data.totalElements
                    });
                    setTransactions(response.data.content || []);
                    setTotalPages(response.data.totalPages || 1);
                    setTotalTransactions(response.data.totalElements || 0);
                } else {
                    console.log('Skipping disputes transactions update - fetchKey changed or no data', {
                        currentKey: lastFetchParamsRef.current,
                        expectedKey: fetchKey,
                        hasData: !!response?.data
                    });
                }
            })
            .catch((error) => {
                if (lastFetchParamsRef.current === fetchKey) {
                    console.error('Error fetching disputes transactions:', error);
                    setTransactions([]);
                }
            })
            .finally(() => {
                // Always reset loading state to prevent stuck loading animations
                setTransactionsLoader(false);
            });

        // Fetch summary
        setSummaryLoader(true);
        const summaryPromise = apiService.getTransactionsSummary({ ...effectiveFilters, intermediateStatuses: [IntermediateStatusType.PENDING_CHARGEBACK, IntermediateStatusType.PENDING_REFUND] })
            .then((response) => {
                // Only update if this fetch is still current
                if (lastFetchParamsRef.current === fetchKey && response?.data) {
                    console.log('Setting disputes summary:', response.data);
                    setTransactionsSummary(response.data);
                } else {
                    console.log('Skipping disputes summary update - fetchKey changed or no data', {
                        currentKey: lastFetchParamsRef.current,
                        expectedKey: fetchKey,
                        hasData: !!response?.data
                    });
                }
            })
            .catch((error) => {
                if (lastFetchParamsRef.current === fetchKey) {
                    console.error('Error fetching disputes transactions summary:', error);
                }
            })
            .finally(() => {
                // Always reset loading state to prevent stuck loading animations
                setSummaryLoader(false);
            });

        // Reset fetching flag when all requests complete
        Promise.allSettled([transactionsPromise, summaryPromise])
            .finally(() => {
                // Always reset flag, even if cancelled
                isFetchingRef.current = false;
            });

        // Cleanup function: if dependencies change, update lastFetchParamsRef
        return () => {
            // Reset fetching flag so new effect can proceed
            isFetchingRef.current = false;
        };
    }, [user, effectiveFilters, currentPage, canFetch])

    const handleTimeFilters = (startDate?: string, endDate?: string) => {
        setFilters((prev) => ({ ...prev, startDate, endDate }));
        setCurrentPage(1);
    };

    const handleTableFilters = (newFilters: Record<string, any>) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilters({});
        setResetTrigger((prev) => !prev);
        setCurrentPage(1);
    };

    const handleRefreshClick = async (transactionId: string) => {
        setRefreshingTransactionId(transactionId);
        const startTime = Date.now();
        try {
            const response = await apiService.refreshTransaction(transactionId);
            const updatedStatus = response.data.status;

            setTransactions((prevTransactions) =>
                prevTransactions?.map((transaction) =>
                    transaction.id === transactionId
                        ? { ...transaction, status: updatedStatus }
                        : transaction
                )
            );
        } catch (e) {
            console.error('Failed to refresh transaction status:', e);
        } finally {
            const elapsedTime = Date.now() - startTime;
            const minimumDuration = 1000;

            const remainingTime = Math.max(0, minimumDuration - elapsedTime);

            setTimeout(() => {
                setRefreshingTransactionId(null);
            }, remainingTime);
        }
    };

    const handleUpdateStatus = async (transactionId: string, newStatus: StatusType) => {
        setRefreshingTransactionId(transactionId);
        const startTime = Date.now();
        try {
            const response = await apiService.updateTransactionStatus(transactionId, newStatus)
            const updatedStatus = response.data.status;
            setTransactions((prevTransactions) =>
                prevTransactions?.map((transaction) =>
                    transaction.id === transactionId
                        ? { ...transaction, status: updatedStatus }
                        : transaction
                )
            );
            errorService.showSuccess('Success', 'Status has been updated');
            setModalVisible(false);
        } catch (e) {
            errorService.showApiError(e);
            setModalVisible(false);
        } finally {
            const elapsedTime = Date.now() - startTime;
            const minimumDuration = 1000;

            const remainingTime = Math.max(0, minimumDuration - elapsedTime);
            setTimeout(() => {
                setRefreshingTransactionId(null);
            }, remainingTime);
        }
    };

    const handleCancel = () => {
        setModalVisible(false);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };
    return (
        <div className="disputes-page page-content">

            <div className="page-header flex-row flex-gap-10 flex-align-start-center">
                <div className='page-title'>
                    Disputes
                </div>

            </div>
            <div className='flex-row flex-gap-10 width-100 flex-wrap'>
                <div className='flex-2-form'>
                    <TableFilters
                        onFiltersChange={handleTableFilters}
                        resetTrigger={resetTrigger}
                        showStatus={false}
                    />
                </div>
                <div className='flex-1-form'>
                    <TransactionsPartFilteredDataItem
                        total={transactionsSummary?.totalAmount || 0}
                        noTransactions={transactionsSummary?.transactionCount || 0}
                        filters={{ ...filters, intermediateStatuses: [IntermediateStatusType.PENDING_CHARGEBACK, IntermediateStatusType.PENDING_REFUND] }}
                        isLoading={summaryLoader}
                        dontShowPDF
                    />
                </div>
            </div>
            <TimeFilters handleFilters={handleTimeFilters} transactionsNo={totalTransactions} resetTrigger={resetTrigger} />
            <Button theme='accent' label='Clear Filters' onClick={resetFilters} />

            <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
            <div className='inner-page flex-column flex-gap-30 width-100'>
                <TableContent columns={columnsMidTransactions} data={transactions || []} isLoading={transactionsLoader} />
            </div>
            <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
            <AppModal
                text={`Are you sure you want to change the STATUS of the transaction to `}
                id={modalData.id}
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                confirmAction={handleUpdateStatus}
                cancelAction={handleCancel}
                isVisible={isModalVisible}
                status={modalData.newStatus as StatusType}
            />
        </div>
    )
}

export default DisputesPage;